/**
 * Shared Voyado Elevate helpers for theme integrations.
 */
if (!window.ElevateCore) {
window.ElevateCore = {
  SESSION_STORAGE_KEY: 'voyado_elevate.session',
  TICKET_STORAGE_PREFIX: 'elevate:ticket:',
  VARIANT_TICKET_STORAGE_PREFIX: 'elevate:variant-ticket:',
  CONTENT_TICKET_STORAGE_PREFIX: 'elevate:content-ticket:',
  MISSING_PRODUCT_KEY_STORAGE: 'voyado_elevate.missing_product_keys',
  TOUCHPOINT_MAX_WIDTH: 749,
  // Coalesce concurrent product-page callers (visit tracking + recommendations) into one request.
  productPageBatches: new Map(),
  sectionRenderCache: new Map(),
  missingProductKeys: new Set(),
  lastClickTicket: null,
  lastClickAt: 0,
  recentAtc: new Map(),
  lastCartLines: new Map(),

  extractSectionId(element) {
    const section = element.closest('.shopify-section');
    return section?.id?.replace('shopify-section-', '') ?? '';
  },

  getTouchpoint() {
    return window.matchMedia(`(max-width: ${this.TOUCHPOINT_MAX_WIDTH}px)`).matches ? 'MOBILE' : 'DESKTOP';
  },

  getViewId() {
    return new URLSearchParams(window.location.search).get('PREVIEW') === 'true' ? 'PREVIEW' : 'PRODUCTION';
  },

  getUserContext() {
    const privacy = window.Shopify?.customerPrivacy;

    if (!privacy) {
      return 'PERSONALIZED';
    }

    const canTrack = typeof privacy.userCanBeTracked === 'function' ? privacy.userCanBeTracked() : true;
    const analyticsAllowed =
      typeof privacy.analyticsProcessingAllowed === 'function'
        ? privacy.analyticsProcessingAllowed()
        : canTrack;

    // No tracking consent: omit keys. Elevate only counts these as Anonymous.
    if (!canTrack && !analyticsAllowed) {
      return 'ANONYMOUS';
    }

    // Analytics ok, personalization not: sessionKey only.
    if (!canTrack) {
      return 'UNPERSONALIZED';
    }

    return 'PERSONALIZED';
  },

  escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  },

  escapeAttr(value) {
    return this.escapeHtml(value);
  },

  readCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  },

  readElevateAppSession() {
    const client = window.elevate?.query?.c;

    if (!client || typeof client.session !== 'function') {
      return null;
    }

    const { customerKey, sessionKey } = client.session();

    if (!customerKey || !sessionKey) {
      return null;
    }

    return { sessionKey, customerKey };
  },

  syncSessionToStorage({ sessionKey, customerKey }) {
    if (!sessionKey || !customerKey) {
      return;
    }

    try {
      localStorage.setItem(
        this.SESSION_STORAGE_KEY,
        JSON.stringify({
          elevateSessionKey: sessionKey,
          elevateCustomerKey: customerKey
        })
      );
    } catch (_error) {
      // Ignore storage errors.
    }
  },

  ensureFallbackSession() {
    const customerStorageKey = 'theme:elevate-fallback-customer';
    const sessionStorageKey = 'theme:elevate-fallback-session';

    let customerKey = null;

    try {
      customerKey = localStorage.getItem(customerStorageKey);
    } catch (_error) {
      // Ignore storage errors.
    }

    if (!customerKey) {
      customerKey = crypto.randomUUID();

      try {
        localStorage.setItem(customerStorageKey, customerKey);
      } catch (_error) {
        // Ignore storage errors.
      }
    }

    let sessionKey = null;

    try {
      sessionKey = sessionStorage.getItem(sessionStorageKey);
    } catch (_error) {
      // Ignore storage errors.
    }

    if (!sessionKey) {
      sessionKey = crypto.randomUUID();

      try {
        sessionStorage.setItem(sessionStorageKey, sessionKey);
      } catch (_error) {
        // Ignore storage errors.
      }
    }

    return { sessionKey, customerKey };
  },

  readSession() {
    const appSession = this.readElevateAppSession();

    if (appSession) {
      this.syncSessionToStorage(appSession);
      return appSession;
    }

    try {
      const raw = localStorage.getItem(this.SESSION_STORAGE_KEY);

      if (raw) {
        const parsed = JSON.parse(raw);

        if (parsed.elevateSessionKey && parsed.elevateCustomerKey) {
          return {
            sessionKey: parsed.elevateSessionKey,
            customerKey: parsed.elevateCustomerKey
          };
        }
      }
    } catch (_error) {
      // Fall through to cookie / generated session.
    }

    const sessionKey = this.readCookie('ve_sk');
    const customerKey = this.readCookie('ve_ck');

    if (sessionKey && customerKey) {
      return { sessionKey, customerKey };
    }

    return this.ensureFallbackSession();
  },

  parseShopifyVariantId(variantKey) {
    if (!variantKey) {
      return null;
    }

    if (/^\d+$/.test(String(variantKey))) {
      return parseInt(variantKey, 10);
    }

    const stripped = String(variantKey).replace(/^v/i, '');

    if (/^\d+$/.test(stripped)) {
      return parseInt(stripped, 10);
    }

    return null;
  },

  parseShopifyProductId(productKey) {
    if (!productKey) {
      return null;
    }

    if (/^\d+$/.test(String(productKey))) {
      return parseInt(productKey, 10);
    }

    const stripped = String(productKey).replace(/^p/i, '');

    if (/^\d+$/.test(stripped)) {
      return parseInt(stripped, 10);
    }

    return null;
  },

  buildProductIdSearchQuery(productIds) {
    return productIds.map((id) => `id:${id}`).join(' OR ');
  },

  // Elevate only has these market/locale pairs. German has no locale, so /de
  // keeps the theme-setting market (NL / nl-NL) until Elevate adds one.
  localeForLanguage(language = window.Shopify?.locale || document.documentElement.lang) {
    const code = String(language || '').slice(0, 2).toLowerCase();
    const pairs = {
      nl: ['NL', 'nl-NL'],
      en: ['INT', 'en-GB'],
      fr: ['BE', 'fr-BE']
    };

    const pair = pairs[code];

    return pair ? { market: pair[0], locale: pair[1] } : null;
  },

  applyLanguageMarket() {
    const resolved = this.localeForLanguage();
    const themeConfig = window.ElevateThemeConfig;

    if (!resolved || !themeConfig) {
      return resolved;
    }

    themeConfig.market = resolved.market;
    themeConfig.locale = resolved.locale;

    return resolved;
  },

  readConfigFromElement(element) {
    const themeConfig = window.ElevateThemeConfig || {};
    const resolved = this.localeForLanguage();

    if (!element) {
      return null;
    }

    return {
      clusterId: element.dataset.clusterId || themeConfig.clusterId,
      market: (resolved?.market || element.dataset.market || themeConfig.market || '').trim(),
      locale: (resolved?.locale || element.dataset.locale || themeConfig.locale || '').trim(),
      limit: parseInt(element.dataset.limit || '10', 10),
      productsPerPage: parseInt(element.dataset.productsPerPage || '24', 10),
      resultsSectionId: element.dataset.resultsSectionId || 'elevate-search-results',
      secondaryResultsSectionId: element.dataset.secondaryResultsSectionId || 'elevate-search-results-grid-secondary',
      contentListId: element.dataset.contentListId || 'content-hits'
    };
  },

  appendIdentityParams(params, userContext = this.getUserContext()) {
    if (userContext === 'ANONYMOUS') {
      return;
    }

    let { sessionKey, customerKey } = this.readSession();

    if (!sessionKey || !customerKey) {
      ({ sessionKey, customerKey } = this.ensureFallbackSession());
    }

    if (sessionKey) {
      params.set('sessionKey', sessionKey);
    }

    if (customerKey && userContext !== 'UNPERSONALIZED') {
      params.set('customerKey', customerKey);
    }
  },

  appendSessionParams(params) {
    const userContext = this.getUserContext();
    params.set('userContext', userContext);
    params.set('viewId', this.getViewId());
    this.appendIdentityParams(params, userContext);
    return userContext;
  },

  mergeProductPageOptions(target, options = {}) {
    if (options.productGroup) {
      target.productGroup = options.productGroup;
    }

    if (options.recommendationLists?.length) {
      const lists = target.recommendationLists ? [...target.recommendationLists] : [];

      for (const list of options.recommendationLists) {
        const index = lists.findIndex((existing) => existing.id === list.id);

        if (index >= 0) {
          lists[index] = { ...lists[index], ...list };
        } else {
          lists.push(list);
        }
      }

      target.recommendationLists = lists;
    }

    if (options.priority) {
      target.priority = options.priority;
    }

    if (options.signal) {
      target.signal = options.signal;
    }

    return target;
  },

  /**
   * Fetch product-page data. Concurrent callers for the same product are batched
   * into a single request (e.g. productGroup visit + recommendationLists).
   */
  productPageCacheKey(config) {
    return [config.clusterId, config.market, config.locale, config.productKey].join('|');
  },

  isMissingProductKey(cacheKey) {
    if (this.missingProductKeys.has(cacheKey)) {
      return true;
    }

    try {
      const stored = JSON.parse(sessionStorage.getItem(this.MISSING_PRODUCT_KEY_STORAGE) || '[]');

      if (stored.includes(cacheKey)) {
        this.missingProductKeys.add(cacheKey);
        return true;
      }
    } catch (_error) {
      // Ignore storage errors.
    }

    return false;
  },

  markMissingProductKey(cacheKey) {
    this.missingProductKeys.add(cacheKey);

    try {
      const stored = JSON.parse(sessionStorage.getItem(this.MISSING_PRODUCT_KEY_STORAGE) || '[]');

      if (!stored.includes(cacheKey)) {
        stored.push(cacheKey);
        sessionStorage.setItem(this.MISSING_PRODUCT_KEY_STORAGE, JSON.stringify(stored.slice(-80)));
      }
    } catch (_error) {
      // Ignore storage errors.
    }
  },

  fetchProductPage(config, options = {}) {
    const key = this.productPageCacheKey(config);

    if (this.isMissingProductKey(key)) {
      return Promise.resolve({ productGroup: null, recommendationLists: [] });
    }

    let batch = this.productPageBatches.get(key);

    if (!batch) {
      batch = {
        config,
        options: {},
        promise: null
      };

      batch.promise = new Promise((resolve, reject) => {
        // Allow sibling PDP components (tracking + recommendations) to register
        // options before the network request fires. Wider than 0 so visit
        // tracking (waits for ElevateCore) can join the recommendations call.
        setTimeout(() => {
          this.productPageBatches.delete(key);
          this.executeProductPageFetch(batch.config, batch.options).then(resolve, reject);
        }, 75);
      });

      this.productPageBatches.set(key, batch);
    }

    this.mergeProductPageOptions(batch.options, options);

    return batch.promise;
  },

  hasQueryContext(config) {
    return Boolean(config?.clusterId && config?.market && config?.locale);
  },

  async executeProductPageFetch(config, options = {}) {
    if (!this.hasQueryContext(config) || !config.productKey) {
      return { productGroup: null, recommendationLists: [] };
    }

    const userContext = this.getUserContext();
    const params = new URLSearchParams({
      market: config.market,
      locale: config.locale,
      touchpoint: this.getTouchpoint(),
      productKey: config.productKey,
      viewId: this.getViewId(),
      userContext
    });

    this.appendIdentityParams(params, userContext);

    const body = {};

    if (options.productGroup) {
      body.productGroup = options.productGroup;
    }

    if (options.recommendationLists?.length) {
      body.recommendationLists = options.recommendationLists;
    }

    const url = `https://${config.clusterId}.elevate-api.cloud/api/storefront/v3/queries/product-page?${params.toString()}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json;charset=utf-8',
        Accept: 'application/json',
        'Accept-Encoding': 'gzip'
      },
      body: JSON.stringify(body),
      signal: options.signal,
      priority: options.priority ?? 'low'
    });

    if (!response.ok) {
      let payload = null;

      try {
        payload = await response.json();
      } catch (_error) {
        payload = null;
      }

      if (response.status === 400 && String(payload?.message || '').includes('does not exist')) {
        this.markMissingProductKey(this.productPageCacheKey(config));
        return { productGroup: null, recommendationLists: [] };
      }

      throw new Error(`Elevate product-page query failed (${response.status})`);
    }

    return response.json();
  },

  async fetchSearchPage(config, options = {}) {
    if (!this.hasQueryContext(config)) {
      throw new Error('Elevate search-page query missing market or locale');
    }

    const params = new URLSearchParams({
      market: config.market,
      locale: config.locale,
      touchpoint: this.getTouchpoint(),
      q: options.q ?? '',
      limit: String(options.limit ?? config.limit ?? 10),
      skip: String(options.skip ?? 0),
      origin: options.origin ?? 'ORGANIC'
    });

    if (options.sort) {
      params.set('sort', options.sort);
    }

    if (options.filters && typeof options.filters === 'object') {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (key.startsWith('f.') && value != null && value !== '') {
          params.set(key, String(value));
        }
      });
    }

    this.appendSessionParams(params);

    const url = `https://${config.clusterId}.elevate-api.cloud/api/storefront/v3/queries/search-page?${params.toString()}`;
    const body = {
      contentLists: [{ id: options.contentListId ?? config.contentListId ?? 'content-hits', limit: 8 }]
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json;charset=utf-8',
        Accept: 'application/json',
        'Accept-Encoding': 'gzip'
      },
      body: JSON.stringify(body),
      signal: options.signal,
      priority: options.priority ?? 'auto'
    });

    if (!response.ok) {
      throw new Error(`Elevate search-page query failed (${response.status})`);
    }

    return response.json();
  },

  async fetchAutocomplete(config, options = {}) {
    if (!this.hasQueryContext(config)) {
      throw new Error('Elevate autocomplete query missing market or locale');
    }

    const params = new URLSearchParams({
      market: config.market,
      locale: config.locale,
      touchpoint: this.getTouchpoint(),
      q: options.q ?? ''
    });

    this.appendSessionParams(params);

    const url = `https://${config.clusterId}.elevate-api.cloud/api/storefront/v3/queries/autocomplete?${params.toString()}`;
    const body = {
      contentLists: [{ id: options.contentListId ?? config.contentListId ?? 'content-hits', limit: 5 }]
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json;charset=utf-8',
        Accept: 'application/json',
        'Accept-Encoding': 'gzip'
      },
      body: JSON.stringify(body),
      signal: options.signal,
      priority: options.priority ?? 'low'
    });

    if (!response.ok) {
      throw new Error(`Elevate autocomplete query failed (${response.status})`);
    }

    return response.json();
  },

  extractProductGroups(productGroups) {
    const productIds = [];
    const tickets = [];

    for (const group of productGroups ?? []) {
      const mainProduct = group.products?.[0];

      if (!mainProduct) {
        continue;
      }

      const shopifyId = this.parseShopifyProductId(mainProduct.key);

      if (!shopifyId) {
        continue;
      }

      productIds.push(shopifyId);
      tickets.push(mainProduct.ticket ?? '');
    }

    return { productIds, tickets };
  },

  extractPrimaryListProducts(data) {
    const { productIds, tickets } = this.extractProductGroups(data.primaryList?.productGroups);
    const contentLists = data.contentLists ?? [];

    this.extractContentItems(contentLists);

    return {
      productIds,
      tickets,
      totalHits: data.primaryList?.totalHits ?? productIds.length,
      effectiveQuery: data.q ?? '',
      relatedSearches: data.relatedSearches ?? [],
      didYouMean: data.didYouMean ?? [],
      autoCorrect: data.autoCorrect ?? null,
      secondaryList: data.secondaryList ?? null,
      contentLists,
      facets: Array.isArray(data.primaryList?.facets) ? data.primaryList.facets : [],
      sort: data.primaryList?.sort ?? null
    };
  },

  extractAutocompleteProducts(data) {
    const { productIds, tickets } = this.extractProductGroups(data.productSuggestions);
    const contentLists = data.contentLists ?? [];

    this.extractContentItems(contentLists);

    return {
      productIds,
      tickets,
      phraseSuggestions: data.phraseSuggestions ?? [],
      recentSearches: data.recentSearches ?? [],
      productSuggestions: data.productSuggestions ?? [],
      contentLists
    };
  },

  priceAmount(price) {
    const value = Number(price?.min ?? price);
    return Number.isFinite(value) ? value : null;
  },

  formatPrice(amount) {
    const value = this.priceAmount(amount);

    if (value == null) {
      return '';
    }

    const currency = window.Shopify?.currency?.active || 'EUR';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency
      }).format(value);
    } catch (_error) {
      return '';
    }
  },

  // Same handle order as snippets/fragrance-title.liquid. Size stays on the title.
  FRAGRANCE_HANDLE_RULES: [
    { needle: 'objets-damsterdam', label: "Objets d'Amsterdam", labelAlt: 'Objets d\u2019Amsterdam', number: '12' },
    { needle: 'violet-muse', label: 'Violet Muse', number: '11' },
    { needle: 'lemon-notes', label: 'Lemon Notes', number: '09' },
    { needle: 'voyage-vetiver', label: 'Voyage Vetiver', labelAlt: 'Voyage Vétiver', number: '07' },
    { needle: 'nuit-des-figues', label: 'Nuit des Figues', number: '08' },
    { needle: 'rock-roses', label: 'Rock Roses', number: '10' },
    { needle: 'poivre-noir-frais', label: 'Poivre Noir Frais', number: '03' },
    { needle: 'lheure-santal', label: "L'Heure Santal", labelAlt: 'L\u2019Heure Santal', number: '13' },
    { needle: 'firewood-green', label: 'Firewood Green', number: '14' },
    { needle: 'rosemarys-touch', label: "Rosemary's Touch", labelAlt: 'Rosemary\u2019s Touch', number: '75' },
    { needle: 'amber-haze', label: 'Amber Haze', number: '15' },
    { needle: 'bergamot-leaf', label: 'Bergamot Leaf', number: '05' },
    { needle: 'flower-water', label: 'Flower Water', number: '20' },
    { needle: 'rose-fumee', label: 'Rose Fumée', labelAlt: 'Rose Fumee', number: '17' },
    { needle: 'terre-fertile', label: 'Terre Fertile', number: '18' },
    { needle: 'suede-spice', label: 'Suede Spice', number: '19' }
  ],

  productHandle(link) {
    const parts = String(link || '').split('?')[0].split('/').filter(Boolean);
    const index = parts.lastIndexOf('products');
    return index >= 0 ? decodeURIComponent(parts[index + 1] || '') : '';
  },

  splitProductTitle(title, link) {
    const handle = this.productHandle(link).toLowerCase();
    const rule = this.FRAGRANCE_HANDLE_RULES.find((item) => handle.includes(item.needle));
    const full = String(title || '').replace(/\s+/g, ' ').trim();

    if (!rule) {
      return { title: full, subtitle: '' };
    }

    const candidates = [...new Set([rule.label, rule.labelAlt].filter(Boolean))];
    let main = full;

    for (const candidate of candidates) {
      if (!main.includes(candidate)) {
        continue;
      }

      main = main.split(candidate).join(' ').replace(/\s+/g, ' ').trim();
      break;
    }

    return {
      title: main || full,
      subtitle: `No.${rule.number} ${rule.label}`
    };
  },

  buildAutocompleteProducts(groups, limit = 8) {
    const cards = [];

    for (const group of groups ?? []) {
      const product = group.products?.[0];

      if (!product?.link || !product.title) {
        continue;
      }

      const href = this.escapeAttr(product.link);
      const split = this.splitProductTitle(product.title, product.link);
      const title = this.escapeHtml(split.title);
      const subtitle = split.subtitle
        ? `<span class="fragrance-subtitle text-subdued">${this.escapeHtml(split.subtitle)}</span>`
        : '';
      const image = product.imageInfo?.images?.[0]?.sources?.[0]?.url || product.imageInfo?.thumbnail || '';
      const amount = this.priceAmount(product.sellingPrice);
      const listAmount = this.priceAmount(product.listPrice);
      const onSale = listAmount != null && amount != null && listAmount > amount;
      const price = this.formatPrice(amount);
      const compare = onSale ? this.formatPrice(listAmount) : '';
      const ticketAttr = product.ticket ? ` data-elevate-ticket="${this.escapeAttr(product.ticket)}"` : '';
      const imageHtml = image
        ? `<img class="product-card__image product-card__image--primary aspect-tall" src="${this.escapeAttr(image)}" alt="${this.escapeAttr(product.title)}" width="600" height="900" loading="lazy">`
        : '';
      const compareHtml = compare
        ? `<compare-at-price class="subheading text-subdued line-through">${this.escapeHtml(compare)}</compare-at-price>`
        : '';
      const priceHtml = price
        ? `<price-list class="price-list justify-center">${compareHtml}<sale-price class="subheading ${onSale ? 'text-on-sale' : 'text-subdued'}">${this.escapeHtml(price)}</sale-price></price-list>`
        : '';

      cards.push(
        `<div class="product-card product-card--info-outside product-card--blends product-card--bordered"${ticketAttr}><div class="product-card__figure"><a href="${href}" class="product-card__media"${ticketAttr}>${imageHtml}</a></div><div class="product-card__info product-card__info--flush"><div class="v-stack gap-2.5 justify-items-center text-center"><div class="v-stack gap-0.5 justify-items-center"><a href="${href}" class="product-title subheading"${ticketAttr}>${title}</a>${subtitle}</div>${priceHtml}</div></div></div>`
      );

      if (cards.length >= limit) {
        break;
      }
    }

    if (!cards.length) {
      return '';
    }

    return `<div class="predictive-search__products">${cards.join('')}</div>`;
  },

  extractSecondaryListProducts(data) {
    const secondary = data.secondaryList ?? null;
    const extracted = this.extractProductGroups(secondary?.productGroups);

    return {
      ...extracted,
      title: secondary?.label ?? secondary?.title ?? ''
    };
  },

  async renderProductsSection(config, productIds, sectionId) {
    if (!productIds.length) {
      return null;
    }

    const cacheKey = `${sectionId}|${productIds.join(',')}`;
    const cached = this.sectionRenderCache?.get(cacheKey);

    if (cached) {
      return cached.cloneNode(true);
    }

    const searchQuery = this.buildProductIdSearchQuery(productIds);
    const renderUrl = `${Shopify.routes.root}search?type=product&q=${encodeURIComponent(searchQuery)}&section_id=${sectionId}`;
    const response = await fetch(renderUrl, { priority: 'high' });
    const tempDiv = new DOMParser().parseFromString(await response.text(), 'text/html');
    const section = tempDiv.querySelector('.shopify-section');

    if (section) {
      this.sectionRenderCache.set(cacheKey, section.cloneNode(true));
    }

    return section;
  },

  attachTickets(root, tickets, productIds = []) {
    const ticketByProductId = new Map();

    productIds.forEach((productId, index) => {
      const ticket = tickets[index];

      if (ticket) {
        ticketByProductId.set(String(productId), ticket);
      }
    });

    root.querySelectorAll('product-card').forEach((card, index) => {
      const productId = card.querySelector('[data-product-id]')?.getAttribute('data-product-id');
      const ticket = productId ? ticketByProductId.get(String(productId)) : tickets[index];

      if (ticket) {
        card.dataset.elevateTicket = ticket;
        card.querySelectorAll('a.product-card__media, a.product-title').forEach((link) => {
          link.dataset.elevateTicket = ticket;
        });

        if (productId) {
          this.persistProductTicket(productId, ticket);
        }
      }
    });
  },

  persistVariantTicket(variantId, ticket) {
    if (!variantId || !ticket) {
      return;
    }

    try {
      sessionStorage.setItem(`${this.VARIANT_TICKET_STORAGE_PREFIX}${variantId}`, ticket);
    } catch (_error) {
      // Ignore storage errors.
    }
  },

  readVariantTicket(variantId) {
    if (!variantId) {
      return null;
    }

    try {
      return sessionStorage.getItem(`${this.VARIANT_TICKET_STORAGE_PREFIX}${variantId}`);
    } catch (_error) {
      return null;
    }
  },

  clearVariantTicket(variantId) {
    if (!variantId) {
      return;
    }

    try {
      sessionStorage.removeItem(`${this.VARIANT_TICKET_STORAGE_PREFIX}${variantId}`);
    } catch (_error) {
      // Ignore storage errors.
    }
  },

  storeProductPageTickets(data, shopifyProductId, currentVariantId = null) {
    const productGroup = data?.productGroup;

    if (productGroup?.products?.length) {
      for (const product of productGroup.products) {
        const parsedProductId = this.parseShopifyProductId(product.key);

        if (parsedProductId && String(parsedProductId) === String(shopifyProductId) && product.ticket) {
          this.persistProductTicket(shopifyProductId, product.ticket);
        }

        for (const variant of product.variants ?? []) {
          if (!variant.ticket) {
            continue;
          }

          const parsedVariantId = this.parseShopifyVariantId(variant.key);

          if (parsedVariantId) {
            this.persistVariantTicket(parsedVariantId, variant.ticket);
          }

          this.persistVariantTicket(variant.key, variant.ticket);
        }
      }
    }

    for (const [variantKey, ticket] of Object.entries(data.tickets ?? {})) {
      if (!ticket) {
        continue;
      }

      const parsedVariantId = this.parseShopifyVariantId(variantKey);

      if (parsedVariantId) {
        this.persistVariantTicket(parsedVariantId, ticket);
      }

      this.persistVariantTicket(variantKey, ticket);
    }

    if (currentVariantId) {
      const variantTicket = this.readVariantTicket(currentVariantId);

      if (variantTicket) {
        this.persistProductTicket(shopifyProductId, variantTicket);
      }
    }
  },

  persistProductTicket(productId, ticket) {
    if (!productId || !ticket) {
      return;
    }

    try {
      sessionStorage.setItem(`${this.TICKET_STORAGE_PREFIX}${productId}`, ticket);
    } catch (_error) {
      // Ignore storage errors.
    }
  },

  readProductTicket(productId) {
    if (!productId) {
      return null;
    }

    try {
      return sessionStorage.getItem(`${this.TICKET_STORAGE_PREFIX}${productId}`);
    } catch (_error) {
      return null;
    }
  },

  clearProductTicket(productId) {
    if (!productId) {
      return;
    }

    try {
      sessionStorage.removeItem(`${this.TICKET_STORAGE_PREFIX}${productId}`);
    } catch (_error) {
      // Ignore storage errors.
    }
  },

  sendTicketNotification(endpoint, ticket, config = window.ElevateThemeConfig) {
    if (!ticket || !config?.clusterId || !config?.market) {
      return;
    }

    const userContext = this.getUserContext();
    const params = new URLSearchParams({
      market: config.market,
      userContext
    });

    this.appendIdentityParams(params, userContext);

    const url = `https://${config.clusterId}.elevate-api.cloud/api/storefront/v3/notifications/${endpoint}?${params.toString()}`;
    const body = JSON.stringify({ ticket });

    // POST only. application/json sendBeacon failed CORS and Elevate stored
    // it as GET. text/plain is CORS-safelisted, so the beacon stays a POST.
    try {
      const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });

      if (navigator.sendBeacon?.(url, blob)) {
        return;
      }
    } catch (_error) {
      // Fall through to fetch.
    }

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body,
      keepalive: true
    }).catch(() => {
      // Non-blocking analytics.
    });
  },

  sendClickNotification(config, ticket) {
    this.sendTicketNotification('click', ticket, config);
  },

  sendAddToCartNotification(ticket, config = window.ElevateThemeConfig) {
    this.sendTicketNotification('add-to-cart', ticket, config);
  },

  snapshotCart(cart) {
    const lines = new Map();

    for (const item of cart?.items ?? []) {
      lines.set(String(item.variant_id), item.quantity);
    }

    this.lastCartLines = lines;
  },

  extractAddedItems(event) {
    const fromDetail = event.detail?.items;
    const cart = event.detail?.cart;

    if (event.detail?.baseEvent === 'elevate:cart-sync') {
      this.snapshotCart(cart);
      return [];
    }

    if (Array.isArray(fromDetail) && fromDetail.length) {
      this.snapshotCart(cart);
      return fromDetail;
    }

    if (event.type !== 'cart:change' || !cart?.items) {
      return [];
    }

    const added = [];

    for (const item of cart.items) {
      const previous = this.lastCartLines.get(String(item.variant_id)) || 0;

      if (item.quantity > previous) {
        added.push(item);
      }
    }

    this.snapshotCart(cart);
    return added;
  },

  resolveTicketForItem(item, event) {
    const variantId = item?.variant_id;
    const productId = item?.product_id;
    const card = event.target?.closest?.('product-card');

    if (card?.dataset.elevateTicket) {
      return {
        ticket: card.dataset.elevateTicket,
        productId: String(productId || card.querySelector('[data-product-id]')?.getAttribute('data-product-id') || ''),
        variantId: variantId ? String(variantId) : null
      };
    }

    if (variantId) {
      const variantTicket = this.readVariantTicket(String(variantId));

      if (variantTicket) {
        return { ticket: variantTicket, productId: String(productId || ''), variantId: String(variantId) };
      }
    }

    if (productId) {
      const ticket = this.readProductTicket(String(productId));

      if (ticket) {
        return { ticket, productId: String(productId), variantId: variantId ? String(variantId) : null };
      }
    }

    return null;
  },

  repairElevateQuery(query) {
    return String(query).replace(/%2526/gi, '%26').replace(/%26(?=[A-Za-z0-9_.-]+=)/g, '&');
  },

  hasElevateQueryParam(query, name) {
    return new RegExp(`(?:^|&)${name}=`).test(query);
  },

  withoutElevateQueryParam(query, name) {
    const prefix = `${name}=`;

    return query.split('&').filter((part) => !part.startsWith(prefix)).join('&');
  },

  withElevateQueryLocale(urlString) {
    if (urlString == null) {
      return urlString;
    }

    let href = String(urlString);

    if (!href.includes('.elevate-api.cloud/')) {
      return href;
    }

    const isProductPage = href.includes('/api/storefront/v3/queries/product-page');
    let hash = '';
    const hashAt = href.indexOf('#');

    if (hashAt !== -1) {
      hash = href.slice(hashAt);
      href = href.slice(0, hashAt);
    }

    const queryAt = href.indexOf('?');
    const base = queryAt === -1 ? href : href.slice(0, queryAt);
    let query = this.repairElevateQuery(queryAt === -1 ? '' : href.slice(queryAt + 1));
    const locale = window.ElevateThemeConfig?.locale;
    const market = window.ElevateThemeConfig?.market;

    if (isProductPage && locale && !this.hasElevateQueryParam(query, 'locale')) {
      query += `${query ? '&' : ''}locale=${encodeURIComponent(locale)}`;
    }

    if (isProductPage && market && !this.hasElevateQueryParam(query, 'market')) {
      query += `${query ? '&' : ''}market=${encodeURIComponent(market)}`;
    }

    // Legacy notify=false collides with userContext. Elevate returns 400.
    if (this.hasElevateQueryParam(query, 'userContext') && this.hasElevateQueryParam(query, 'notify')) {
      query = this.withoutElevateQueryParam(query, 'notify');
    }

    return `${base}${query ? `?${query}` : ''}${hash}`;
  },

  rewriteElevateFetchInput(input) {
    if (typeof input === 'string') {
      return this.withElevateQueryLocale(input);
    }

    if (typeof URL !== 'undefined' && input instanceof URL) {
      return this.withElevateQueryLocale(input.toString());
    }

    if (typeof Request !== 'undefined' && input instanceof Request) {
      const nextUrl = this.withElevateQueryLocale(input.url);

      return nextUrl !== input.url ? new Request(nextUrl, input) : input;
    }

    return input;
  },

  initElevateRequestGuard() {
    if (window.__elevateRequestGuardInitialized) {
      return;
    }

    window.__elevateRequestGuardInitialized = true;

    const originalFetch = window.fetch.bind(window);

    window.fetch = (input, init) => originalFetch(this.rewriteElevateFetchInput(input), init);

    const originalOpen = XMLHttpRequest.prototype.open;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      if (url != null) {
        url = window.ElevateCore.withElevateQueryLocale(url);
      }

      return originalOpen.call(this, method, url, ...rest);
    };
  },

  notifyAddToCartForItem(item, event) {
    const resolved = this.resolveTicketForItem(item, event);

    if (!resolved?.ticket) {
      return;
    }

    const dedupeKey = `${resolved.ticket}:${resolved.variantId || resolved.productId || ''}`;
    const now = Date.now();
    const lastSent = this.recentAtc.get(dedupeKey);

    if (lastSent && now - lastSent < 2000) {
      return;
    }

    this.recentAtc.set(dedupeKey, now);
    this.sendAddToCartNotification(resolved.ticket);
  },

  handleCartAdd(event) {
    for (const item of this.extractAddedItems(event)) {
      this.notifyAddToCartForItem(item, event);
    }
  },

  handleVariantAdd(event) {
    this.handleCartAdd(event);
  },

  initAddToCartNotifications() {
    if (window.__elevateAddToCartInitialized || !window.ElevateThemeConfig?.clusterId) {
      return;
    }

    window.__elevateAddToCartInitialized = true;

    const handle = (event) => {
      window.ElevateCore?.handleCartAdd(event);
    };

    document.addEventListener('variant:add', handle, true);
    document.addEventListener('cart:change', handle, true);
  },

  initVariantTicketSync() {
    if (window.__elevateVariantTicketSyncInitialized) {
      return;
    }

    window.__elevateVariantTicketSyncInitialized = true;

    document.addEventListener('variant:change', (event) => {
      const variantId = event.detail?.variant?.id;
      const productId = document.querySelector('elevate-product-page')?.dataset.productId;

      if (!variantId || !productId) {
        return;
      }

      const variantTicket = this.readVariantTicket(String(variantId));

      if (variantTicket) {
        this.persistProductTicket(productId, variantTicket);
      }
    });
  },

  initSessionSync() {
    if (window.__elevateSessionSyncInitialized) {
      return;
    }

    window.__elevateSessionSyncInitialized = true;

    const sync = () => {
      const appSession = this.readElevateAppSession();

      if (appSession) {
        this.syncSessionToStorage(appSession);
      }
    };

    sync();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', sync);
    }

    window.addEventListener('load', sync);

    let attempts = 0;
    const intervalId = window.setInterval(() => {
      sync();
      attempts += 1;

      if (attempts >= 30 || this.readElevateAppSession()) {
        window.clearInterval(intervalId);
      }
    }, 100);
  },

  isProductCardLink(element) {
    return Boolean(element?.closest?.('a.product-card__media, a.product-title, a[href]'));
  },

  notifyClickFromEvent(event) {
    const ticketEl = event.target.closest?.('[data-elevate-ticket]');

    if (ticketEl?.matches?.('product-card') && !this.isProductCardLink(event.target)) {
      return;
    }

    const link = event.target.closest?.('a[href]');
    const productId =
      ticketEl?.dataset.productId ||
      link?.getAttribute?.('data-product-id') ||
      ticketEl?.querySelector?.('[data-product-id]')?.getAttribute('data-product-id');
    const ticket =
      ticketEl?.dataset.elevateTicket ||
      this.readProductTicket(productId) ||
      this.readContentTicket(link?.href || link?.getAttribute?.('href'));

    if (!ticket) {
      return;
    }

    const now = Date.now();

    if (this.lastClickTicket === ticket && now - this.lastClickAt < 800) {
      return;
    }

    this.lastClickTicket = ticket;
    this.lastClickAt = now;
    this.sendClickNotification(window.ElevateThemeConfig, ticket);

    if (productId) {
      this.persistProductTicket(productId, ticket);
    }
  },

  initClickNotifications() {
    if (window.__elevateClickInitialized || !window.ElevateThemeConfig?.clusterId) {
      return;
    }

    window.__elevateClickInitialized = true;

    const handle = (event) => {
      window.ElevateCore?.notifyClickFromEvent(event);
    };

    document.addEventListener('pointerdown', handle, true);
    document.addEventListener('click', handle, true);
  },

  bindClickTracking(_root, _config) {
    this.initClickNotifications();
  },

  buildSearchUrl(searchUrl, query, origin) {
    const url = new URL(searchUrl, window.location.origin);
    url.searchParams.set('q', query);

    if (origin) {
      url.searchParams.set('origin', origin);
    }

    return `${url.pathname}${url.search}`;
  },

  buildPhraseSuggestionLinks(phrases, searchUrl) {
    if (!phrases?.length) {
      return '';
    }

    return phrases
      .map((phrase) => {
        const term = phrase.q ?? '';
        const href = this.buildSearchUrl(searchUrl, term, 'PHRASE_SUGGEST');
        const display = this.escapeHtml(phrase.highlighted ?? term).replace(/\{([^}]+)\}/g, '<mark>$1</mark>');

        const ticketAttr = phrase.ticket ? ` data-elevate-ticket="${this.escapeAttr(phrase.ticket)}"` : '';

        return `<li><a href="${this.escapeAttr(href)}" class="link-reversed"${ticketAttr}>${display}</a></li>`;
      })
      .join('');
  },

  buildRecentSearchLinks(searches, searchUrl) {
    if (!searches?.length) {
      return '';
    }

    return searches
      .map((item) => {
        const term = item.q ?? '';
        const href = this.buildSearchUrl(searchUrl, term, 'RECENT_SEARCH');

        const ticketAttr = item.ticket ? ` data-elevate-ticket="${this.escapeAttr(item.ticket)}"` : '';

        return `<li><a href="${this.escapeAttr(href)}" class="link-reversed"${ticketAttr}>${this.escapeHtml(term)}</a></li>`;
      })
      .join('');
  },

  buildRelatedSearchLinks(relatedSearches, searchUrl, origin = 'RELATED_SEARCHES') {
    if (!relatedSearches?.length) {
      return '';
    }

    return relatedSearches
      .map((item) => {
        const href = this.buildSearchUrl(searchUrl, item.q ?? '', origin);

        const ticketAttr = item.ticket ? ` data-elevate-ticket="${this.escapeAttr(item.ticket)}"` : '';

        return `<li><a href="${this.escapeAttr(href)}" class="link-reversed"${ticketAttr}>${this.escapeHtml(item.q ?? '')}</a></li>`;
      })
      .join('');
  },

  buildSearchMetaMarkup({ autoCorrect, didYouMean, q, searchUrl }) {
    const parts = [];
    const safeQuery = this.escapeHtml(q);

    if (autoCorrect?.q && autoCorrect.q !== q) {
      const href = this.buildSearchUrl(searchUrl, autoCorrect.q, 'UNDO_AUTO_CORRECT');
      parts.push(
        `<p class="text-subdued">${autoCorrect.originalTotalHits ?? 0} results for "${safeQuery}". Showing results for <a href="${this.escapeAttr(href)}" class="link">${this.escapeHtml(autoCorrect.q)}</a>.</p>`
      );
    }

    if (didYouMean?.length) {
      const suggestions = didYouMean
        .map((item) => {
          const href = this.buildSearchUrl(searchUrl, item.q ?? '', 'DID_YOU_MEAN');

          return `<a href="${this.escapeAttr(href)}" class="link">${this.escapeHtml(item.q ?? '')}</a>`;
        })
        .join(', ');
      parts.push(`<p class="text-subdued">Did you mean ${suggestions}?</p>`);
    }

    return parts.join('');
  },

  normalizeContentPath(href) {
    if (!href) {
      return '';
    }

    try {
      const url = new URL(href, window.location.origin);
      return url.pathname.replace(/\/$/, '') || '/';
    } catch (_error) {
      return String(href).split('?')[0].replace(/\/$/, '') || '';
    }
  },

  resolveContentHref(link) {
    if (!link) {
      return '';
    }

    if (/^https?:/i.test(link)) {
      return link;
    }

    const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');

    if (link.startsWith('/')) {
      if (root && root !== '/' && !link.startsWith(`${root}/`) && link !== root) {
        return `${root}${link}`;
      }

      return link;
    }

    return `${root}/${link}`;
  },

  persistContentTicket(link, ticket) {
    if (!link || !ticket) {
      return;
    }

    const paths = new Set([this.normalizeContentPath(link), this.normalizeContentPath(this.resolveContentHref(link))]);

    try {
      for (const path of paths) {
        if (path) {
          sessionStorage.setItem(`${this.CONTENT_TICKET_STORAGE_PREFIX}${path}`, ticket);
        }
      }
    } catch (_error) {
      // Ignore storage errors.
    }
  },

  readContentTicket(href) {
    const path = this.normalizeContentPath(href);

    if (!path) {
      return null;
    }

    try {
      const exact = sessionStorage.getItem(`${this.CONTENT_TICKET_STORAGE_PREFIX}${path}`);

      if (exact) {
        return exact;
      }

      const bare = path.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/)/, '');

      if (bare !== path) {
        return sessionStorage.getItem(`${this.CONTENT_TICKET_STORAGE_PREFIX}${bare}`);
      }
    } catch (_error) {
      return null;
    }

    return null;
  },

  getHiddenCollectionHandles() {
    const raw = window.ElevateThemeConfig?.hiddenCollections ?? '';

    if (Array.isArray(raw)) {
      return raw.map((handle) => String(handle).trim().toLowerCase()).filter(Boolean);
    }

    return String(raw)
      .split(',')
      .map((handle) => handle.trim().toLowerCase())
      .filter(Boolean);
  },

  contentItemCollectionHandle(item) {
    const href = item?.link || '';
    const path = this.normalizeContentPath(this.resolveContentHref(href) || href);
    const match = path.match(/\/collections\/([^/]+)/i);

    if (!match) {
      return '';
    }

    try {
      return decodeURIComponent(match[1]).toLowerCase();
    } catch (_error) {
      return match[1].toLowerCase();
    }
  },

  isHiddenCollectionContent(item) {
    const handles = this.getHiddenCollectionHandles();

    if (!handles.length) {
      return false;
    }

    const handle = this.contentItemCollectionHandle(item);

    return Boolean(handle) && handles.includes(handle);
  },

  extractContentItems(contentLists) {
    const items = [];

    for (const list of contentLists ?? []) {
      for (const item of list.items ?? []) {
        if (!item?.ticket || !item.link || this.isHiddenCollectionContent(item)) {
          continue;
        }

        this.persistContentTicket(item.link, item.ticket);
        items.push(item);
      }
    }

    return items;
  },

  buildContentMarkup(contentLists, heading = '') {
    const items = this.extractContentItems(contentLists);

    if (!items.length) {
      return '';
    }

    const headingHtml = heading
      ? `<p class="subheading text-subdued">${this.escapeHtml(heading)}</p>`
      : '';
    const links = items
      .map((item) => {
        const href = this.resolveContentHref(item.link);
        const title = item.title || item.link;
        const imageSource = item.image?.sources?.[0]?.url;
        const image = imageSource
          ? `<div class="overflow-hidden shrink-0"><img src="${this.escapeAttr(imageSource)}" alt="${this.escapeAttr(item.image.alt || title)}" width="200" height="200"></div>`
          : '';

        return `<a href="${this.escapeAttr(href)}" class="predictive-search__item-result group" data-elevate-ticket="${this.escapeAttr(item.ticket)}">${image}<p class="h6 link-reversed">${this.escapeHtml(title)}</p></a>`;
      })
      .join('');

    return `${headingHtml}<div class="predictive-search__collections">${links}</div>`;
  },

  buildContentLinks(contentLists) {
    return this.buildContentMarkup(contentLists);
  }
};
}

if (window.ElevateThemeConfig?.clusterId) {
  window.ElevateCore.applyLanguageMarket();
  window.ElevateCore.initElevateRequestGuard();
  window.ElevateCore.initSessionSync();
  window.ElevateCore.initAddToCartNotifications();
  window.ElevateCore.initVariantTicketSync();
  window.ElevateCore.initClickNotifications();
}
